/**
 * Desktop "Browse aisles" panel (build spec §4.3).
 *
 * Departments and aisles are in the page. Shelves and "Popular in this aisle" for a department
 * load once from the Section Rendering API (`/collections/{department}?section_id=cfs-menu-panel`)
 * and are cached for the page lifetime. Fetching starts on pointer hover (before the intent
 * delay) or focus, and all departments' shelves prefetch when idle after the first open.
 * "Popular in this aisle" loads per aisle from `cfs-menu-popular`. A skeleton shows while
 * a department loads.
 *
 * Opens on click of [data-cfs-aisles-trigger] (desktop only; on mobile the trigger is the
 * drill-down menu's) and on hover intent over department links in the header nav row.
 * Keyboard: ↑/↓ within a column, →/← between columns, Esc closes, Tab stays inside.
 */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const SECTION_ID = 'cfs-menu-panel';
const HOVER_INTENT_MS = 180;
const HOVER_CLOSE_MS = 350;

/** @type {Map<string, Promise<Element | null>>} */
const cache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const popularCache = new Map();

/** @param {string} aisleUrl */
function loadPopular(aisleUrl) {
  if (!popularCache.has(aisleUrl)) {
    const url = new URL(aisleUrl, window.location.origin);
    url.searchParams.set('section_id', 'cfs-menu-popular');
    popularCache.set(
      aisleUrl,
      fetch(url.toString())
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
        .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cfs-menu-popular]'))
        .catch(() => {
          popularCache.delete(aisleUrl);
          return null;
        })
    );
  }
  return /** @type {Promise<Element | null>} */ (popularCache.get(aisleUrl));
}

/** @param {string} departmentUrl */
export function loadDepartmentDetail(departmentUrl) {
  if (!cache.has(departmentUrl)) {
    const url = new URL(departmentUrl, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    const promise = fetch(url.toString())
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cfs-menu-detail]'))
      .catch((error) => {
        cache.delete(departmentUrl);
        console.warn('[cfs-aisles] Could not load', departmentUrl, error);
        return null;
      });
    cache.set(departmentUrl, promise);
  }
  return /** @type {Promise<Element | null>} */ (cache.get(departmentUrl));
}

class CfsAislesPanel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;
  #openedByHover = false;
  #department = 0;
  #aisle = 0;
  #hoverTimer = 0;
  #prefetched = false;
  #closeTimer = 0;
  /** @type {Map<number, Element>} */
  #details = new Map();

  connectedCallback() {
    this.dialog = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-aisles-dialog]'));
    this.detailSlot = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-aisles-detail]'));
    this.skeleton = /** @type {HTMLElement} */ (this.querySelector('[data-cfs-aisles-skeleton]'));
    this.departmentLinks = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('[data-department-index]')]);

    for (const trigger of document.querySelectorAll('[data-cfs-aisles-trigger]')) {
      trigger.setAttribute('aria-controls', this.id);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.addEventListener('click', this.#onTriggerClick);
    }

    // Hover intent on department links in the header nav row.
    for (const link of document.querySelectorAll('.cfs-header__nav-link')) {
      const index = this.#departmentIndexForUrl(/** @type {HTMLAnchorElement} */ (link).href);
      if (!index) continue;
      link.addEventListener('mouseenter', () => {
        loadDepartmentDetail(/** @type {HTMLAnchorElement} */ (link).href);
        this.#hoverOpen(index, /** @type {HTMLElement} */ (link));
      });
      link.addEventListener('mouseleave', this.#scheduleHoverClose);
    }
    this.dialog.addEventListener('mouseenter', () => clearTimeout(this.#closeTimer));
    this.dialog.addEventListener('mouseleave', this.#scheduleHoverClose);

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('mouseover', this.#onPointerOver);
    this.addEventListener('focusin', this.#onFocusIn);
    DESKTOP.addEventListener('change', () => !DESKTOP.matches && this.close(false));
  }

  get isOpen() {
    return !this.hidden;
  }

  /** @param {string} href */
  #departmentIndexForUrl(href) {
    const path = new URL(href, window.location.origin).pathname;
    const link = this.departmentLinks.find((a) => new URL(a.href).pathname === path);
    return link ? Number(link.dataset.departmentIndex) : 0;
  }

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    if (!DESKTOP.matches) return; // mobile: handled by the drill-down menu
    event.preventDefault();
    const trigger = /** @type {HTMLElement} */ (event.currentTarget);
    if (this.isOpen && !this.#openedByHover) {
      this.close();
    } else {
      this.open({ trigger, focus: true });
    }
  };

  /**
   * @param {number} index
   * @param {HTMLElement} link
   */
  #hoverOpen(index, link) {
    if (!DESKTOP.matches) return;
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      if (this.isOpen) {
        this.#selectDepartment(index);
      } else {
        this.open({ department: index, trigger: link, focus: false, byHover: true });
      }
    }, HOVER_INTENT_MS);
  }

  #scheduleHoverClose = () => {
    clearTimeout(this.#hoverTimer);
    if (!this.#openedByHover) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.close(false), HOVER_CLOSE_MS);
  };

  /**
   * @param {{ department?: number, trigger?: HTMLElement | null, focus?: boolean, byHover?: boolean }} [options]
   */
  open({ department, trigger = null, focus = true, byHover = false } = {}) {
    this.#trigger = trigger ?? document.querySelector('[data-cfs-aisles-trigger]');
    this.#openedByHover = byHover;
    // Sit just below the bar that opened it (full header or compact sticky bar).
    const bar = this.#trigger?.closest('.cfs-compact, .cfs-header__bar');
    this.style.setProperty('--cfs-aisles-top', `${Math.max(0, Math.round(bar?.getBoundingClientRect().bottom ?? 0))}px`);
    this.hidden = false;
    document.documentElement.classList.add('cfs-aisles-open');
    for (const t of document.querySelectorAll('[data-cfs-aisles-trigger]')) t.setAttribute('aria-expanded', 'true');

    this.#selectDepartment(department || this.#department || 1);
    if (focus) this.#departmentLink(this.#department)?.focus();

    // Shelves for every department are small (~3 KB gzipped): fetch them when idle after the
    // first open so later departments open from memory. Popular products stay on demand.
    if (!this.#prefetched) {
      this.#prefetched = true;
      const idle = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 200));
      idle(() => this.departmentLinks.forEach((a) => loadDepartmentDetail(a.href)));
    }

  }

  /** @param {boolean} [returnFocus=true] */
  close(returnFocus = true) {
    if (!this.isOpen) return;
    clearTimeout(this.#hoverTimer);
    clearTimeout(this.#closeTimer);
    this.hidden = true;
    this.#openedByHover = false;
    document.documentElement.classList.remove('cfs-aisles-open');
    for (const t of document.querySelectorAll('[data-cfs-aisles-trigger]')) t.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.#trigger?.focus();
  }

  /** @param {number} index */
  #departmentLink(index) {
    return this.departmentLinks.find((a) => Number(a.dataset.departmentIndex) === index);
  }

  /** @param {number} index */
  async #selectDepartment(index) {
    if (!index) return;
    const changed = index !== this.#department;
    this.#department = index;

    for (const link of this.departmentLinks) {
      const on = Number(link.dataset.departmentIndex) === index;
      link.setAttribute('aria-expanded', String(on));
      link.classList.toggle('is-active', on);
    }
    for (const list of this.querySelectorAll('[data-department-aisles]')) {
      /** @type {HTMLElement} */ (list).hidden = Number(/** @type {HTMLElement} */ (list).dataset.departmentAisles) !== index;
    }
    if (changed) this.#aisle = 0;

    let detail = this.#details.get(index);
    if (!detail) {
      this.#showDetail(null);
      const link = this.#departmentLink(index);
      const loaded = link ? await loadDepartmentDetail(link.href) : null;
      if (this.#department !== index) return; // moved on while loading
      if (loaded) {
        detail = /** @type {Element} */ (document.importNode(loaded, true));
        this.#details.set(index, detail);
      }
    }
    this.#showDetail(detail ?? null);
    this.#selectAisle(this.#aisle || 1);
  }

  /** @param {Element | null} detail */
  #showDetail(detail) {
    for (const child of [...this.detailSlot.children]) {
      if (child !== this.skeleton) child.remove();
    }
    this.skeleton.hidden = Boolean(detail);
    if (detail) this.detailSlot.append(detail);
  }

  /** @param {number} index */
  #selectAisle(index) {
    this.#aisle = index;
    const aisles = this.querySelector(`[data-department-aisles="${this.#department}"]`);
    for (const link of aisles?.querySelectorAll('[data-aisle-index]') ?? []) {
      const on = Number(/** @type {HTMLElement} */ (link).dataset.aisleIndex) === index;
      link.setAttribute('aria-expanded', String(on));
      link.classList.toggle('is-active', on);
    }
    const detail = this.#details.get(this.#department);
    for (const block of detail?.querySelectorAll('[data-aisle-index]') ?? []) {
      const on = Number(/** @type {HTMLElement} */ (block).dataset.aisleIndex) === index;
      /** @type {HTMLElement} */ (block).hidden = !on;
      if (on) this.#fillPopular(block);
    }
  }

  /** @param {Element} aisleBlock */
  async #fillPopular(aisleBlock) {
    const column = /** @type {HTMLElement | null} */ (aisleBlock.querySelector('[data-cfs-popular-url]'));
    if (!column || column.dataset.loaded) return;
    column.dataset.loaded = 'loading';
    const products = await loadPopular(column.dataset.cfsPopularUrl ?? '');
    if (products) {
      column.append(document.importNode(products, true));
      column.dataset.loaded = 'true';
    } else {
      delete column.dataset.loaded;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-cfs-aisles-close]')) this.close();
  };

  /** @param {MouseEvent} event */
  #onPointerOver = (event) => {
    const link = /** @type {HTMLElement} */ (event.target).closest?.('a');
    if (!link) return;
    if (link.dataset.departmentIndex) {
      loadDepartmentDetail(/** @type {HTMLAnchorElement} */ (link).href); // start fetching before the intent delay
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = window.setTimeout(() => this.#selectDepartment(Number(link.dataset.departmentIndex)), 90);
    } else if (link.dataset.aisleIndex) {
      clearTimeout(this.#hoverTimer);
      this.#hoverTimer = window.setTimeout(() => this.#selectAisle(Number(link.dataset.aisleIndex)), 90);
    }
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const link = /** @type {HTMLElement} */ (event.target);
    if (link.dataset?.departmentIndex) this.#selectDepartment(Number(link.dataset.departmentIndex));
    else if (link.dataset?.aisleIndex) this.#selectAisle(Number(link.dataset.aisleIndex));
  };

  /** Focusable links/buttons in a column element, visible ones only. */
  /** @param {Element | null | undefined} column */
  #items(column) {
    if (!column) return [];
    return /** @type {HTMLElement[]} */ (
      [...column.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.getClientRects().length > 0)
    );
  }

  #columns() {
    const detail = this.#details.get(this.#department);
    const aisleBlock = detail?.querySelector(`[data-aisle-index="${this.#aisle}"]`);
    return [
      this.querySelector('[data-cfs-column="departments"]'),
      this.querySelector(`[data-department-aisles="${this.#department}"]`),
      aisleBlock?.querySelector('[data-cfs-column="shelves"]'),
      aisleBlock?.querySelector('.cfs-menu-detail__popular'),
    ];
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const active = /** @type {HTMLElement} */ (document.activeElement);

    if (event.key === 'Tab') {
      const all = this.#items(this.dialog);
      if (all.length === 0) return;
      const first = all[0];
      const last = all[all.length - 1];
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const columns = this.#columns();
    const col = columns.findIndex((c) => c?.contains(active));
    if (col === -1) return;
    event.preventDefault();

    const items = this.#items(columns[col]);
    const i = items.indexOf(active);

    if (event.key === 'ArrowDown') items[Math.min(items.length - 1, i + 1)]?.focus();
    else if (event.key === 'ArrowUp') items[Math.max(0, i - 1)]?.focus();
    else if (event.key === 'Home') items[0]?.focus();
    else if (event.key === 'End') items[items.length - 1]?.focus();
    else if (event.key === 'ArrowRight') {
      const nextCol = columns.slice(col + 1).find((c) => this.#items(c).length > 0);
      if (!nextCol) return;
      const nextItems = this.#items(nextCol);
      // Land on the selected item of the next column when there is one, else its first item.
      (nextItems.find((el) => el.classList.contains('is-active')) ?? nextItems[0])?.focus();
    } else if (event.key === 'ArrowLeft') {
      const prevCol = columns.slice(0, col).reverse().find((c) => this.#items(c).length > 0);
      if (!prevCol) return;
      const prevItems = this.#items(prevCol);
      (prevItems.find((el) => el.classList.contains('is-active')) ?? prevItems[0])?.focus();
    }
  };
}

// Close when clicking anywhere outside the panel and its triggers.
document.addEventListener('click', (event) => {
  const panel = /** @type {CfsAislesPanel | null} */ (document.querySelector('cfs-aisles-panel'));
  const target = /** @type {HTMLElement} */ (event.target);
  if (!panel?.isOpen) return;
  if (panel.contains(target) || target.closest('[data-cfs-aisles-trigger]')) return;
  panel.close(false);
});

if (!customElements.get('cfs-aisles-panel')) customElements.define('cfs-aisles-panel', CfsAislesPanel);
